<template>
  <div class="filters-panel">
    <div class="field">
      <label class="label">Номер рейса</label>
      <input v-model="tripInput" class="input" type="text" placeholder="Начало номера" @input="onTripInput">
    </div>

    <div class="field field-classification">
      <label class="label">Класс события</label>
      <div class="select-area">
        <button class="dropdown-summary" type="button" @click="toggleClassificationDropdown">
          <span>{{ classificationSummary }}</span><span class="chevron">{{ openClassification ? '\u25B2' : '\u25BC' }}</span>
        </button>
        <div v-if="openClassification" :key="'cls-' + classificationKey" class="dropdown-menu">
          <label class="dropdown-option">
            <input type="checkbox" :checked="filters.classifications.length === 0" @change="selectAllClassifications" />
            <span>Все классы</span>
          </label>
          <label v-for="opt in classificationOptions" :key="opt.value" class="dropdown-option" @click="handleClassificationClick($event, opt.value)">
            <input type="checkbox" :checked="filters.classifications.length === 0 || filters.classifications.includes(opt.value)" :disabled="filters.classifications.length === 1 && filters.classifications.includes(opt.value)" @change="toggleClassification(opt.value)" />
            <span class="status-dot" :style="dotStyle(opt)"></span>
            <span>{{ opt.label }}</span>
          </label>
        </div>
      </div>
      <div v-if="filters.classifications.length > 1" class="tag-list">
        <button v-for="opt in selectedClassificationOptions" :key="opt.value" class="tag" @click="removeClassification(opt.value)">{{ opt.label }} x</button>
      </div>
    </div>

    <div class="field field-work-status">
      <label class="label">Рабочий статус рейса</label>
      <div class="select-area">
        <button class="dropdown-summary" type="button" @click="toggleWorkStatusDropdown">
          <span>{{ workStatusSummary }}</span><span class="chevron">{{ openWorkStatus ? '\u25B2' : '\u25BC' }}</span>
        </button>
        <div v-if="openWorkStatus" :key="'ws-' + workStatusKey" class="dropdown-menu">
          <label class="dropdown-option">
            <input type="checkbox" :checked="filters.workStatusIds.length === 0" :disabled="filters.workStatusIds.length === 0" @change="selectAllWorkStatuses" />
            <span>Все активные статусы</span>
          </label>
          <label v-for="opt in workStatusOptions" :key="opt.value" class="dropdown-option" @click="handleWorkStatusClick($event, opt.value)">
            <input type="checkbox" :checked="filters.workStatusIds.length === 0 || filters.workStatusIds.includes(opt.value)" :disabled="filters.workStatusIds.length === 1 && filters.workStatusIds.includes(opt.value)" @change="toggleWorkStatus(opt.value)" />
            <span class="status-dot" :style="dotStyle(opt)"></span>
            <span>{{ opt.label }}</span>
          </label>
          <div v-if="optionsError" class="empty-options">Не удалось загрузить статусы</div>
          <div v-else-if="!workStatusOptions.length" class="empty-options">Нет доступных статусов</div>
        </div>
      </div>
      <div v-if="filters.workStatusIds.length > 1" class="tag-list">
        <button v-for="opt in selectedWorkStatusOptions" :key="opt.value" class="tag" @click="removeWorkStatus(opt.value)">{{ opt.label }} x</button>
      </div>
    </div>

    <div class="field">
      <label class="label">Регистрация простоя</label>
      <select class="input select" :value="filters.status" @change="update({ status: $event.target.value })">
        <option v-for="option in registrationOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
      </select>
    </div>

    <div class="divider" />

    <div class="field">
      <label class="label">Операция</label>
      <select class="input select" :value="filters.operationType" @change="update({ operationType: $event.target.value })">
        <option v-for="option in operationOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
      </select>
    </div>

    <div class="field field-manager">
      <label class="label">Менеджер</label>
      <div class="select-area">
        <button class="dropdown-summary" type="button" @click="toggleManagerDropdown">
          <span>{{ managerSummary }}</span><span class="chevron">{{ openManager ? '\u25B2' : '\u25BC' }}</span>
        </button>
        <div v-if="openManager" :key="'mgr-' + managerKey" class="dropdown-menu">
          <label class="dropdown-option">
            <input type="checkbox" :checked="filters.responsibleIds.length === 0" :disabled="filters.responsibleIds.length === 0" @change="selectAllManagers" />
            <span>Все менеджеры</span>
          </label>
          <label v-for="opt in managerOptions" :key="opt.value" class="dropdown-option" @click="handleManagerClick($event, opt.value)">
            <input type="checkbox" :checked="filters.responsibleIds.includes(opt.value)" :disabled="filters.responsibleIds.length === 1 && filters.responsibleIds.includes(opt.value)" @change="toggleManager(opt.value)" />
            <span>{{ opt.label }}</span>
          </label>
          <label class="dropdown-option" @click="handleManagerClick($event, 'none')">
            <input type="checkbox" :checked="filters.responsibleIds.includes('none')" :disabled="filters.responsibleIds.length === 1 && filters.responsibleIds.includes('none')" @change="toggleManager('none')" />
            <span>Без менеджера</span>
          </label>
          <div v-if="optionsError" class="empty-options">Не удалось загрузить менеджеров</div>
          <div v-else-if="!managerOptions.length" class="empty-options">Нет доступных менеджеров</div>
        </div>
      </div>
      <div v-if="filters.responsibleIds.length > 1" class="tag-list">
        <button v-for="opt in selectedManagerOptions" :key="opt.value" class="tag" @click="removeManager(opt.value)">{{ opt.label }} x</button>
      </div>
    </div>

    <div class="field">
      <label class="label">План с</label>
      <input class="input" type="date" :value="filters.plannedDateFrom" :max="filters.plannedDateTo || undefined" @change="update({ plannedDateFrom: $event.target.value, datePeriod: '' })">
    </div>
    <div class="field">
      <label class="label">План по</label>
      <input class="input" type="date" :value="filters.plannedDateTo" :min="filters.plannedDateFrom || undefined" @change="update({ plannedDateTo: $event.target.value, datePeriod: '' })">
    </div>

    <div class="date-presets-block">
      <span class="presets-title">Быстрый выбор периода</span>
      <div class="chip-group date-presets">
        <button
          v-for="preset in datePresets"
          :key="preset.label"
          :class="['chip', 'preset-chip', { active: preset.active }]"
          @click="applyPreset(preset)"
        >{{ preset.label }}</button>
      </div>
    </div>

    <label class="checkbox-label">
      <input type="checkbox" :checked="filters.includeStale" @change="update({ includeStale: $event.target.checked })">
      <span>Показывать устаревшие</span>
    </label>
    <button class="btn-clear" type="button" @click="$emit('reset')">Сбросить фильтры</button>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { CLASSIFICATION_OPTIONS, OPERATION_OPTIONS, REGISTRATION_OPTIONS } from '../downtimeOptions.mjs';
import { PERIOD_PRESETS, resolvePeriodRange } from '../downtimePeriods.mjs';

export default {
  name: 'DowntimesFilters',
  props: {
    filters: { type: Object, required: true },
    workStatusOptions: { type: Array, default: () => [] },
    managerOptions: { type: Array, default: () => [] },
    optionsError: { type: Boolean, default: false },
  },
  emits: ['update', 'reset'],
  setup(props, { emit }) {
    const tripInput = ref(props.filters.tripIdPrefix || '');
    let tripTimer = null;
    const openWorkStatus = ref(false);
    const openClassification = ref(false);
    const openManager = ref(false);
    const workStatusKey = ref(0);
    const classificationKey = ref(0);
    const managerKey = ref(0);

    const classificationOptions = CLASSIFICATION_OPTIONS;
    const dotStyle = (opt) => ({ background: opt.color });

    watch(() => props.filters.tripIdPrefix, (value) => { tripInput.value = value || ''; });
    const update = (patch) => emit('update', patch);
    const onTripInput = () => {
      clearTimeout(tripTimer);
      tripTimer = setTimeout(() => update({ tripIdPrefix: tripInput.value }), 500);
    };

    const workStatusValues = computed(() => props.workStatusOptions.map((o) => o.value));

    const setWorkStatus = (next) => update({ workStatusIds: next });
    const normalizeWorkStatus = (nextValue) => {
      if (nextValue.length === 0) return;
      const values = workStatusValues.value;
      if (nextValue.length === values.length && values.every((item) => nextValue.includes(item))) {
        setWorkStatus([]);
        return;
      }
      setWorkStatus(nextValue);
    };
    const toggleWorkStatus = (val) => {
      const cur = props.filters.workStatusIds;
      const currentValue = cur.length === 0 ? workStatusValues.value : cur;
      const next = currentValue.includes(val) ? currentValue.filter((v) => v !== val) : [...currentValue, val];
      normalizeWorkStatus(next);
    };
    const handleWorkStatusClick = (event, val) => {
      if (!event.shiftKey) return;
      event.preventDefault();
      event.stopPropagation();
      setWorkStatus([val]);
    };
    const selectAllWorkStatuses = () => setWorkStatus([]);
    const removeWorkStatus = (val) => {
      const cur = props.filters.workStatusIds.length === 0 ? workStatusValues.value : props.filters.workStatusIds;
      const next = cur.filter((item) => item !== val);
      if (next.length > 0) normalizeWorkStatus(next);
    };

    const toggleWorkStatusDropdown = () => {
      openWorkStatus.value = !openWorkStatus.value;
      if (openWorkStatus.value) workStatusKey.value++;
    };

    const classificationValues = CLASSIFICATION_OPTIONS.map((o) => o.value);
    const setClassification = (next) => update({ classifications: next });
    const normalizeClassification = (nextValue) => {
      if (nextValue.length === 0) return;
      if (nextValue.length === classificationValues.length && classificationValues.every((item) => nextValue.includes(item))) {
        setClassification([]);
        return;
      }
      setClassification(nextValue);
    };
    const toggleClassification = (val) => {
      const cur = props.filters.classifications;
      const currentValue = cur.length === 0 ? classificationValues : cur;
      const next = currentValue.includes(val) ? currentValue.filter((v) => v !== val) : [...currentValue, val];
      normalizeClassification(next);
    };
    const handleClassificationClick = (event, val) => {
      if (!event.shiftKey) return;
      event.preventDefault();
      event.stopPropagation();
      setClassification([val]);
    };
    const selectAllClassifications = () => setClassification([]);
    const removeClassification = (val) => {
      const cur = props.filters.classifications.length === 0 ? classificationValues : props.filters.classifications;
      const next = cur.filter((item) => item !== val);
      if (next.length > 0) normalizeClassification(next);
    };
    const toggleClassificationDropdown = () => {
      openClassification.value = !openClassification.value;
      if (openClassification.value) classificationKey.value++;
    };

    const MANAGER_NONE_VALUE = 'none';
    const setResponsible = (next) => update({ responsibleIds: next });
    const toggleManager = (val) => {
      const cur = props.filters.responsibleIds;
      if (!cur.length) {
        setResponsible([val]);
        return;
      }
      const next = cur.includes(val) ? cur.filter((v) => v !== val) : [...cur, val];
      if (next.length) setResponsible(next);
    };
    const handleManagerClick = (event, val) => {
      if (!event.shiftKey) return;
      event.preventDefault();
      event.stopPropagation();
      setResponsible([val]);
    };
    const selectAllManagers = () => setResponsible([]);
    const removeManager = (val) => {
      const cur = props.filters.responsibleIds;
      if (cur.length <= 1) return;
      setResponsible(cur.filter((item) => item !== val));
    };
    const toggleManagerDropdown = () => {
      openManager.value = !openManager.value;
      if (openManager.value) managerKey.value++;
    };

    const selectedWorkStatusOptions = computed(() => {
      const cur = props.filters.workStatusIds;
      if (!cur.length) return props.workStatusOptions;
      return props.workStatusOptions.filter((o) => cur.includes(o.value));
    });
    const selectedClassificationOptions = computed(() => {
      const cur = props.filters.classifications;
      if (!cur.length) return CLASSIFICATION_OPTIONS;
      return CLASSIFICATION_OPTIONS.filter((o) => cur.includes(o.value));
    });
    const selectedManagerOptions = computed(() => {
      const cur = props.filters.responsibleIds;
      const items = props.managerOptions.filter((o) => cur.includes(o.value));
      return cur.includes(MANAGER_NONE_VALUE)
        ? [...items, { value: MANAGER_NONE_VALUE, label: 'Без менеджера' }]
        : items;
    });
    const managerSummary = computed(() => {
      const cur = props.filters.responsibleIds;
      if (!cur.length) return 'Все менеджеры';
      if (cur.length === 1) {
        if (cur[0] === MANAGER_NONE_VALUE) return 'Без менеджера';
        return props.managerOptions.find((o) => o.value === cur[0])?.label || 'Выбрано: 1';
      }
      return `Выбрано: ${cur.length}`;
    });
    const workStatusSummary = computed(() => {
      const cur = props.filters.workStatusIds;
      if (!cur.length) return 'Все активные статусы';
      if (cur.length === 1) return props.workStatusOptions.find((o) => o.value === cur[0])?.label || '';
      return `Выбрано: ${cur.length}`;
    });
    const classificationSummary = computed(() => {
      const cur = props.filters.classifications;
      if (!cur.length) return 'Все классы';
      if (cur.length === 1) return CLASSIFICATION_OPTIONS.find((o) => o.value === cur[0])?.label || '';
      return `Выбрано: ${cur.length}`;
    });

    const datePresets = computed(() => {
      const from = props.filters.plannedDateFrom;
      const to = props.filters.plannedDateTo;
      const cur = props.filters.datePeriod;
      return PERIOD_PRESETS.map((p) => {
        const range = resolvePeriodRange(p.key) || { from: '', to: '' };
        const active = cur === p.key || (!cur && from === range.from && to === range.to);
        return { ...p, range, active };
      });
    });

    const applyPreset = (preset) => {
      const cur = props.filters.datePeriod;
      const from = props.filters.plannedDateFrom;
      const to = props.filters.plannedDateTo;
      const isActive = cur === preset.key || (!cur && from === preset.range.from && to === preset.range.to);
      if (isActive) {
        update({ datePeriod: '', plannedDateFrom: '', plannedDateTo: '' });
        return;
      }
      update({ datePeriod: preset.key, plannedDateFrom: preset.range.from, plannedDateTo: preset.range.to });
    };

    const handleClickOutside = (e) => {
      if (openWorkStatus.value && !e.target.closest('.field-work-status')) openWorkStatus.value = false;
      if (openClassification.value && !e.target.closest('.field-classification')) openClassification.value = false;
      if (openManager.value && !e.target.closest('.field-manager')) openManager.value = false;
    };

    onMounted(() => window.addEventListener('click', handleClickOutside));
    onBeforeUnmount(() => {
      window.removeEventListener('click', handleClickOutside);
      clearTimeout(tripTimer);
    });

    return {
      tripInput,
      openWorkStatus,
      openClassification,
      openManager,
      workStatusKey,
      classificationKey,
      managerKey,
      classificationOptions,
      registrationOptions: REGISTRATION_OPTIONS,
      operationOptions: OPERATION_OPTIONS,
      selectedWorkStatusOptions,
      selectedClassificationOptions,
      selectedManagerOptions,
      workStatusSummary,
      classificationSummary,
      managerSummary,
      dotStyle,
      update,
      onTripInput,
      toggleWorkStatus,
      handleWorkStatusClick,
      selectAllWorkStatuses,
      removeWorkStatus,
      toggleWorkStatusDropdown,
      toggleClassification,
      handleClassificationClick,
      selectAllClassifications,
      removeClassification,
      toggleClassificationDropdown,
      toggleManager,
      handleManagerClick,
      selectAllManagers,
      removeManager,
      toggleManagerDropdown,
      datePresets,
      applyPreset,
    };
  },
};
</script>

<style scoped>
.filters-panel { display: flex; flex-direction: column; gap: 12px; padding: 12px; font-size: 14px; color: #344054; }
.field { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.label { font-size: 13px; font-weight: 600; color: #555; margin-bottom: 2px; }
.input { box-sizing: border-box; width: 100%; min-height: 32px; padding: 6px 10px; border: 1px solid #ccc; border-radius: 4px; background: #fff; color: #333; font-size: 14px; }
.input.select { cursor: pointer; }

.select-area { position: relative; }

.dropdown-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font-size: 14px;
  cursor: pointer;
  min-height: 18px;
  box-sizing: border-box;
  gap: 6px;
}

.chevron { flex-shrink: 0; color: #007bff; font-size: 12px; }

.dropdown-menu {
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  max-height: 260px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid #d7dce2;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
  user-select: none;
}

.dropdown-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}

.dropdown-option:hover { background: rgba(224, 187, 72, 0.18); }
.dropdown-option input { accent-color: #007bff; }

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.empty-options { padding: 8px; color: #98a2b3; font-size: 12px; }

.chip-group { display: flex; flex-wrap: wrap; gap: 4px; }

.chip {
  padding: 3px 8px;
  border: 1px solid #ccc;
  border-radius: 12px;
  font-size: 13px;
  background: #fff;
  color: #333;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.chip.active { background: #007bff; border-color: #007bff; color: #fff; }
.chip:hover { border-color: #007bff; }

.date-presets-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f7f8fa;
}

.presets-title { font-size: 13px; font-weight: 600; color: #555; }

.tag-list { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }

.tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 6px;
  border: 1px solid #007bff;
  border-radius: 10px;
  font-size: 12px;
  cursor: pointer;
  color: #007bff;
  background: #e8f0fe;
  line-height: 1.4;
}

.divider { height: 1px; background: #e5e7eb; }

.checkbox-label { display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 14px; }
.checkbox-label input { accent-color: #007bff; }

.btn-clear {
  padding: 7px 12px;
  border: 1px solid #b42318;
  border-radius: 4px;
  background: #fff;
  color: #b42318;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-clear:hover { background: #b42318; color: #fff; }
</style>
